/**
 * ข้อมูลจำลองสำหรับกลุ่มหน้าจอที่ 2 — การจัดการประเภทสมาชิก
 *
 * แหล่งข้อมูลจำลองแห่งเดียวของกลุ่มนี้ ไม่มีการเก็บลง localStorage
 * (localStorage ใช้เฉพาะการจำค่าที่ผู้ใช้ตั้งเอง ผ่าน usePersistentState)
 *
 * ค่าที่ยังเป็นค่าสมมติและรอของจริง (PROGRESS-REPORT ข้อ 8.1 และ 8.4):
 *   — รายชื่อโดเมนทั้งหมดลงท้าย .example ⛔ ขวางการขึ้นระบบ
 *   — อายุการยืนยันสิทธิ์ 365 วัน เตือนล่วงหน้า 30 วัน
 *   — โควตา วิตามินรวม 500 ชิ้น/ปี · ผลิตภัณฑ์อื่น 300 ชิ้น/ปี
 *   — นโยบายพนักงานเกษียณยังไม่มีคำตอบ
 */

import type { QueueStatus } from '../components';

/* ------------------------------------------------------- ประเภทสมาชิก */

/** ประเภทสมาชิกที่ต้องผ่านการอนุมัติ ตาม SRS 3.2 */
export type MemberType =
  | 'ptt-employee'      // พนักงาน ปตท. (ปัจจุบัน) — ราคาพนักงาน ปตท./อินโนบิก
  | 'ptt-group'         // พนักงานกลุ่ม ปตท. (ปัจจุบัน) — ราคากลุ่ม ปตท.
  | 'ptt-retired'       // พนักงานเกษียณ — สิทธิ์ "ตามที่กำหนด" ยังไม่มีนโยบาย
  | 'b2b-corporate'     // กลุ่มองค์กร (นิติบุคคล)
  | 'b2b-government'    // ราชการ / รัฐวิสาหกิจ
  | 'b2b-shop'          // ร้านค้า / คลินิก / Wellness Center
  | 'pharmacy-retail'
  | 'pharmacy-wholesale'
  | 'pharmacy-chain';

export const MEMBER_TYPE_LABEL: Record<MemberType, string> = {
  'ptt-employee': 'พนักงาน ปตท.',
  'ptt-group': 'พนักงานกลุ่ม ปตท.',
  'ptt-retired': 'พนักงานเกษียณ',
  'b2b-corporate': 'กลุ่มองค์กร (นิติบุคคล)',
  'b2b-government': 'ราชการ / รัฐวิสาหกิจ',
  'b2b-shop': 'ร้านค้า / คลินิก / Wellness',
  'pharmacy-retail': 'ร้านขายยา — Retail',
  'pharmacy-wholesale': 'ร้านขายยา — Wholesale',
  'pharmacy-chain': 'ร้านขายยา — Chain',
};

/** กลุ่มใหญ่ ใช้จัดกลุ่มในตัวกรองและในหน้าโควตา */
export const MEMBER_TYPE_FAMILY: Record<MemberType, 'ptt' | 'b2b' | 'pharmacy'> = {
  'ptt-employee': 'ptt', 'ptt-group': 'ptt', 'ptt-retired': 'ptt',
  'b2b-corporate': 'b2b', 'b2b-government': 'b2b', 'b2b-shop': 'b2b',
  'pharmacy-retail': 'pharmacy', 'pharmacy-wholesale': 'pharmacy', 'pharmacy-chain': 'pharmacy',
};

/** ส่วนลดตั้งต้นของแต่ละประเภท (%) — ค่าจริงมาจากโครงสร้างราคา (FR-ADM-16, Phase 2) */
export const MEMBER_TYPE_DISCOUNT: Partial<Record<MemberType, number>> = {
  'ptt-employee': 30,
  'ptt-group': 20,
  'ptt-retired': 20,
};

/* ------------------------------------------------------------ โดเมน */

export interface EmailDomain {
  id: string;
  /** โดเมน เช่น pttor.example — ยังเป็นค่าสมมติทั้งหมด */
  domain: string;
  /** ชื่อบริษัทที่โดเมนนี้เป็นของ */
  company: string;
  memberType: MemberType;
  enabled: boolean;
  /** นับโดเมนย่อยเข้าด้วยหรือไม่ (เช่น hr.pttor.example) — ข้อ 8.1 หัวข้อ 3 */
  includeSubdomains: boolean;
  /** จำนวนผู้ใช้ที่ยืนยันสิทธิ์ด้วยโดเมนนี้ */
  verifiedUsers: number;
  /** จำนวนที่การยืนยันยังไม่หมดอายุ */
  activeUsers: number;
  addedAt: string;
  addedBy: string;
  /** เหตุผลที่ปิดใช้งาน (ถ้าปิด) */
  disabledReason?: string;
  disabledAt?: string;
  /** สิ่งที่เลือกทำกับสมาชิกเดิมเมื่อปิดโดเมน */
  disabledPolicy?: DisablePolicy;
}

/** ทางเลือกที่ต้องตัดสินตอนปิดโดเมน — ไม่มีค่าเริ่มต้น ต้องเลือกเสมอ */
export type DisablePolicy =
  | 'keep-until-expiry'   // คงสิทธิ์เดิมไว้จนหมดอายุตามรอบ แล้วยืนยันใหม่ไม่ได้
  | 'revoke-now'          // ยกเลิกสิทธิ์ทันที ลดเป็นสมาชิกทั่วไป
  | 'require-documents';  // คงสิทธิ์ แต่บังคับให้แนบเอกสารในการยืนยันรอบถัดไป

export const DISABLE_POLICY_LABEL: Record<DisablePolicy, string> = {
  'keep-until-expiry': 'คงสิทธิ์เดิมไว้จนหมดอายุตามรอบ แล้วยืนยันใหม่ด้วยโดเมนนี้ไม่ได้',
  'revoke-now': 'ยกเลิกสิทธิ์ทันที ลดเป็นสมาชิกทั่วไป',
  'require-documents': 'คงสิทธิ์ไว้ แต่รอบยืนยันถัดไปต้องแนบเอกสารแทนโดเมน',
};

export const DISABLE_POLICY_EFFECT: Record<DisablePolicy, string> = {
  'keep-until-expiry': 'ไม่มีสมาชิกเสียสิทธิ์ทันที แต่ทุกคนจะทยอยหมดสิทธิ์ตามวันหมดอายุของตัวเอง',
  'revoke-now': 'สมาชิกเสียราคาพนักงานทันทีในวันนี้ และคำสั่งซื้อที่ยังไม่ชำระจะคิดราคาใหม่',
  'require-documents': 'สมาชิกยังใช้สิทธิ์ต่อได้ แต่คิวอนุมัติจะมีงานเพิ่มตามจำนวนคนที่ต้องยืนยันใหม่',
};

export const DOMAINS: EmailDomain[] = [
  { id: 'd1', domain: 'pttor.example', company: 'ปตท. น้ำมันและการค้าปลีก', memberType: 'ptt-employee', enabled: true, includeSubdomains: true, verifiedUsers: 1284, activeUsers: 1192, addedAt: '2569-02-14', addedBy: 'ธนกร แสงทวี' },
  { id: 'd2', domain: 'pttplc.example', company: 'ปตท. (บริษัทแม่)', memberType: 'ptt-employee', enabled: true, includeSubdomains: true, verifiedUsers: 2417, activeUsers: 2260, addedAt: '2569-02-14', addedBy: 'ธนกร แสงทวี' },
  { id: 'd3', domain: 'innobic.example', company: 'อินโนบิก (เอเซีย)', memberType: 'ptt-employee', enabled: true, includeSubdomains: false, verifiedUsers: 342, activeUsers: 338, addedAt: '2569-02-14', addedBy: 'ธนกร แสงทวี' },
  { id: 'd4', domain: 'gcgroup.example', company: 'พีทีที โกลบอล เคมิคอล', memberType: 'ptt-group', enabled: true, includeSubdomains: true, verifiedUsers: 986, activeUsers: 902, addedAt: '2569-03-02', addedBy: 'ธนกร แสงทวี' },
  { id: 'd5', domain: 'thaioil.example', company: 'ไทยออยล์', memberType: 'ptt-group', enabled: true, includeSubdomains: false, verifiedUsers: 731, activeUsers: 688, addedAt: '2569-03-02', addedBy: 'ธนกร แสงทวี' },
  { id: 'd6', domain: 'irpc.example', company: 'ไออาร์พีซี', memberType: 'ptt-group', enabled: true, includeSubdomains: false, verifiedUsers: 604, activeUsers: 561, addedAt: '2569-03-02', addedBy: 'ธนกร แสงทวี' },
  { id: 'd7', domain: 'gpsc.example', company: 'โกลบอล เพาเวอร์ ซินเนอร์ยี่', memberType: 'ptt-group', enabled: true, includeSubdomains: false, verifiedUsers: 218, activeUsers: 203, addedAt: '2569-04-18', addedBy: 'พิมพ์ชนก อารีย์' },
  { id: 'd8', domain: 'ptttank.example', company: 'พีทีที แทงค์ เทอร์มินัล', memberType: 'ptt-group', enabled: true, includeSubdomains: false, verifiedUsers: 74, activeUsers: 71, addedAt: '2569-05-06', addedBy: 'พิมพ์ชนก อารีย์' },
  {
    id: 'd9', domain: 'pttdigital-old.example', company: 'พีทีที ดิจิทัล (โดเมนเดิม)', memberType: 'ptt-group',
    enabled: false, includeSubdomains: false, verifiedUsers: 141, activeUsers: 39, addedAt: '2569-02-14', addedBy: 'ธนกร แสงทวี',
    disabledReason: 'บริษัทเปลี่ยนไปใช้โดเมนใหม่ pttdigital.example เมื่อ 1 ก.ค. 2569',
    disabledAt: '2569-07-04', disabledPolicy: 'keep-until-expiry',
  },
];

/** อีเมลทั่วไปที่ห้ามใช้ยืนยันสิทธิ์ เพราะเป็นกล่องกลางขององค์กร ไม่ผูกกับบุคคล */
export const BLOCKED_LOCAL_PARTS: Array<{ value: string; note?: string }> = [
  { value: 'info' }, { value: 'hr', note: 'กล่องกลางฝ่ายบุคคล' }, { value: 'admin' },
  { value: 'contact' }, { value: 'support' }, { value: 'noreply' },
  { value: 'sales' }, { value: 'marketing' }, { value: 'webmaster' },
  { value: 'office', note: 'พบการใช้ซ้ำจากหลายคนในบริษัทเดียว' },
];

/* --------------------------------------------------- คำขอเปลี่ยนประเภท */

/** เหตุผลที่คำขอนี้ต้องใช้คนตรวจ แม้จะมีการยืนยันด้วยโดเมนแล้ว */
export type ManualReason =
  | 'domain-not-listed'   // โดเมนไม่อยู่ในรายการ
  | 'retired'             // พนักงานเกษียณ — ยังไม่มีนโยบาย
  | 'b2b'                 // กลุ่ม B2B ต้องแนบเอกสาร
  | 'pharmacy'            // ร้านขายยาต้องแนบเอกสาร
  | 'domain-disabled';    // ยืนยันด้วยโดเมนที่ถูกปิดไปแล้ว

export const MANUAL_REASON_LABEL: Record<ManualReason, string> = {
  'domain-not-listed': 'โดเมนไม่อยู่ในรายการ',
  retired: 'พนักงานเกษียณ',
  b2b: 'กลุ่ม B2B ต้องตรวจเอกสาร',
  pharmacy: 'ร้านขายยาต้องตรวจเอกสาร',
  'domain-disabled': 'ยืนยันด้วยโดเมนที่ปิดใช้งานแล้ว',
};

export const MANUAL_REASON_HINT: Record<ManualReason, string> = {
  'domain-not-listed': 'ตรวจว่าบริษัทนี้อยู่ในเครือหรือไม่ ถ้าอยู่ให้เพิ่มโดเมนในหน้าจัดการโดเมนเพื่อไม่ให้คนถัดไปต้องเข้าคิว',
  retired: 'ยังไม่มีนโยบายพนักงานเกษียณ — ห้ามตัดสินเองจนได้คำตอบ (PROGRESS-REPORT 8.4)',
  b2b: 'ต้องมี ภพ.20 · หนังสือรับรอง · หน้าสมุดบัญชี · ข้อมูลผู้ประสานงาน ครบทั้ง 4 อย่าง',
  pharmacy: 'ต้องมีใบทะเบียนร้านค้า และข้อมูลใบกำกับภาษีที่ตรงกับชื่อร้าน',
  'domain-disabled': 'โดเมนนี้ถูกปิดแล้ว ตรวจว่าผู้ขอยังทำงานที่บริษัทนั้นจริงหรือไม่',
};

export interface DocumentAttachment {
  id: string;
  /** ชนิดเอกสารตาม SRS 3.2 */
  kind: 'employee-card' | 'por-20' | 'company-cert' | 'bankbook' | 'shop-license' | 'tax-invoice' | 'other';
  label: string;
  url: string;
  uploadedAt: string;
  /** ระบบอ่านข้อมูลจากเอกสารได้บางส่วนหรือไม่ */
  extracted?: Record<string, string>;
}

export interface TypeRequest {
  id: string;
  code: string;
  status: QueueStatus;
  requestedType: MemberType;
  /** ประเภทเดิมก่อนขอเปลี่ยน */
  currentType: 'general' | MemberType;
  manualReason: ManualReason;
  member: {
    id: string; name: string; memberCode: string;
    phone: string; email: string;
    /** โดเมนที่ผู้ขอใช้ — ถ้าไม่อยู่ในรายการจะเป็น null ที่ฝั่ง matchedDomainId */
    emailDomain: string;
    matchedDomainId: string | null;
  };
  /** ข้อมูลที่กรอกในฟอร์ม (ต่างกันตามประเภท — FR-MTYPE-02) */
  fields: Array<{ label: string; value: string }>;
  documents: DocumentAttachment[];
  submittedAt: string;
  ageHours: number;
  assigneeId: string | null;
  /** สิ่งที่ระบบตรวจพบและเจ้าหน้าที่ควรเห็นก่อนตัดสิน */
  flags: string[];
  decision?: { at: string; byId: string; reasonLabel?: string; detail?: string };
}

const docSvg = (label: string, w = 620, h = 860) =>
  'data:image/svg+xml;utf8,' + encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><defs><pattern id="p" width="14" height="14" patternUnits="userSpaceOnUse"><path d="M0 14 L14 0" stroke="#C9CDD6" stroke-width="1"/></pattern></defs><rect width="${w}" height="${h}" fill="#F5F6F9"/><rect width="${w}" height="${h}" fill="url(#p)"/><rect x="44" y="44" width="${w - 88}" height="${h - 88}" fill="#FFFFFF" stroke="#C9CDD6"/><text x="${w / 2}" y="${h / 2}" text-anchor="middle" font-family="monospace" font-size="18" fill="#5B6270">${label}</text></svg>`,
  );

const REQ_NAMES = [
  'ประภาส ชัยมงคล', 'อารยา เลิศวัฒนา', 'สมชาย ตรีเนตร', 'ณัฐวุฒิ พัฒนกิจ',
  'กมลวรรณ สินธุ์เจริญ', 'ภาณุพงศ์ วงศ์สุวรรณ', 'จิราพร แก้วประเสริฐ', 'ดนุพล อัครเดช',
  'สุพรรณี รุ่งเรือง', 'วีรยุทธ บวรรัตน์', 'ชนิดา ภูมิใจ', 'ธีระ จันทร์กระจ่าง',
];

const COMPANIES_UNLISTED = [
  'พีทีที รีเทล บิสซิเนส', 'สยาม แมนเนจเมนท์ โฮลดิ้ง', 'เอนเนอร์ยี่ คอมเพล็กซ์',
  'พีทีที ดิจิทัล โซลูชั่น', 'ไทย ลู้บ เบล็นดิ้ง',
];

function buildRequests(): TypeRequest[] {
  const out: TypeRequest[] = [];
  const now = new Date('2026-08-21T09:30:00');

  for (let i = 0; i < 186; i += 1) {
    /* สัดส่วน: หลังใช้การยืนยันโดเมน คิวเหลือเฉพาะ 5 กรณีนี้ */
    const reasons: ManualReason[] = ['domain-not-listed', 'b2b', 'pharmacy', 'retired', 'domain-disabled'];
    const manualReason = reasons[i % 5 === 4 ? (i % 10 === 4 ? 3 : 4) : i % 5]!;

    const requestedType: MemberType =
      manualReason === 'retired' ? 'ptt-retired'
        : manualReason === 'b2b' ? (['b2b-corporate', 'b2b-government', 'b2b-shop'] as MemberType[])[i % 3]!
          : manualReason === 'pharmacy' ? (['pharmacy-retail', 'pharmacy-wholesale', 'pharmacy-chain'] as MemberType[])[i % 3]!
            : i % 2 === 0 ? 'ptt-employee' : 'ptt-group';

    const ageHours = 3 + ((i * 13) % 120);
    let status: QueueStatus = ageHours > 72 ? 'overdue' : i % 19 === 0 ? 'review' : 'pending';
    if (i % 11 === 5) status = 'approved';
    if (i % 23 === 7) status = 'rejected';

    const name = REQ_NAMES[i % REQ_NAMES.length]!;
    const isPtt = MEMBER_TYPE_FAMILY[requestedType] === 'ptt';
    const company = manualReason === 'domain-not-listed' ? COMPANIES_UNLISTED[i % COMPANIES_UNLISTED.length]!
      : manualReason === 'domain-disabled' ? 'พีทีที ดิจิทัล (โดเมนเดิม)'
        : isPtt ? 'ปตท. น้ำมันและการค้าปลีก' : 'บริษัท ' + name.split(' ')[0]! + ' จำกัด';

    const emailDomain = manualReason === 'domain-not-listed' ? `${['pttrb', 'smh', 'enco', 'pttdigital', 'tlb'][i % 5]!}.example`
      : manualReason === 'domain-disabled' ? 'pttdigital-old.example'
        : isPtt ? 'pttor.example' : 'gmail.com';

    const documents: DocumentAttachment[] = [];
    if (isPtt) {
      documents.push({ id: `${i}-card`, kind: 'employee-card', label: 'รูปถ่ายบัตรพนักงาน', url: docSvg('employee card'), uploadedAt: '2569-08-14', extracted: { 'รหัสพนักงาน': 'E' + (420000 + i * 7), 'บริษัท': company } });
    }
    if (MEMBER_TYPE_FAMILY[requestedType] === 'b2b' || MEMBER_TYPE_FAMILY[requestedType] === 'pharmacy') {
      documents.push(
        { id: `${i}-por20`, kind: 'por-20', label: 'ภพ.20', url: docSvg('por 20'), uploadedAt: '2569-08-14', extracted: { 'เลขทะเบียน': '0105' + (500000 + i * 11) } },
        { id: `${i}-cert`, kind: 'company-cert', label: 'หนังสือรับรองบริษัท', url: docSvg('company cert'), uploadedAt: '2569-08-14' },
      );
      if (i % 3 !== 0) documents.push({ id: `${i}-bank`, kind: 'bankbook', label: 'หน้าสมุดบัญชี', url: docSvg('bankbook'), uploadedAt: '2569-08-14' });
      if (MEMBER_TYPE_FAMILY[requestedType] === 'pharmacy') {
        documents.push({ id: `${i}-lic`, kind: 'shop-license', label: 'ใบทะเบียนร้านค้า', url: docSvg('shop license'), uploadedAt: '2569-08-14' });
      }
    }

    const flags: string[] = [];
    if (manualReason === 'domain-not-listed') {
      flags.push(`โดเมน ${emailDomain} ไม่อยู่ในรายการ — ถ้าบริษัทนี้อยู่ในเครือ ให้เพิ่มโดเมนเพื่อไม่ให้คนถัดไปต้องเข้าคิว`);
    }
    if (manualReason === 'retired') {
      flags.push('ยังไม่มีนโยบายพนักงานเกษียณ — สิทธิ์และวิธีพิสูจน์ตัวตนยังไม่ได้ข้อสรุป (PROGRESS-REPORT 8.4)');
    }
    if (manualReason === 'domain-disabled') {
      flags.push('โดเมน pttdigital-old.example ถูกปิดเมื่อ 4 ก.ค. 2569 เพราะบริษัทย้ายไปโดเมนใหม่');
    }
    if (i % 13 === 3) flags.push('เลขบัตรประชาชนนี้เคยยื่นคำขอด้วยชื่อบริษัทอื่นเมื่อ 5 เดือนก่อน');
    if (i % 17 === 6 && documents.length) flags.push('เอกสารที่แนบมาไม่ครบตามรายการที่ประเภทนี้ต้องมี');
    if (i % 29 === 9) flags.push('รหัสพนักงานในบัตรซ้ำกับสมาชิกอีกคนที่ได้รับอนุมัติแล้ว');

    const submitted = new Date(now.getTime() - ageHours * 3600 * 1000);

    out.push({
      id: 'r' + i,
      code: 'MTR-' + (32000 + i),
      status,
      requestedType,
      currentType: 'general',
      manualReason,
      member: {
        id: 'm' + i, name, memberCode: 'MB-' + (7000 + i * 53),
        phone: '08' + String(10000000 + i * 37).slice(0, 8),
        email: name.split(' ')[0]!.toLowerCase() + '.' + String(i).padStart(3, '0') + '@' + emailDomain,
        emailDomain,
        matchedDomainId: manualReason === 'domain-disabled' ? 'd9' : manualReason === 'domain-not-listed' ? null : isPtt ? 'd1' : null,
      },
      fields: isPtt
        ? [
            { label: 'ชื่อบริษัท', value: company },
            { label: 'รหัสพนักงาน', value: 'E' + (420000 + i * 7) },
            { label: 'อีเมลบริษัท', value: name.split(' ')[0]!.toLowerCase() + '@' + emailDomain },
            ...(manualReason === 'retired' ? [{ label: 'ปีที่เกษียณ', value: String(2560 + (i % 8)) }] : []),
          ]
        : [
            { label: 'ชื่อนิติบุคคล', value: company },
            { label: 'เลขทะเบียนนิติบุคคล', value: '0105' + (500000 + i * 11) },
            { label: 'ผู้ประสานงาน', value: name },
            { label: 'เบอร์ติดต่อ', value: '02-' + String(2000000 + i * 13).slice(0, 7) },
          ],
      documents,
      submittedAt: submitted.toISOString(),
      ageHours,
      assigneeId: i % 3 === 0 ? 'u1' : ['u1', 'u2', 'u3', 'u4'][i % 4]!,
      flags,
      decision: status === 'approved' || status === 'rejected'
        ? {
            at: new Date(submitted.getTime() + 6 * 3600 * 1000).toISOString(),
            byId: 'u1',
            reasonLabel: status === 'rejected' ? 'เอกสารที่แนบมาไม่ครบตามรายการที่ประเภทนี้ต้องมี' : undefined,
          }
        : undefined,
    });
  }
  return out;
}

export const TYPE_REQUESTS: TypeRequest[] = buildRequests();

/* ------------------------------------------------- การยืนยันสิทธิ์รายปี */

/** อายุการยืนยันสิทธิ์และการเตือนล่วงหน้า — ค่าสมมติ รอยืนยัน (PROGRESS-REPORT 8.4) */
export const VERIFICATION_DAYS = 365;
export const VERIFICATION_WARN_DAYS = 30;

export interface Verification {
  id: string;
  member: { id: string; name: string; memberCode: string; email: string; phone: string };
  memberType: MemberType;
  domainId: string | null;
  /** วิธีที่ยืนยันครั้งล่าสุด */
  method: 'domain' | 'document';
  verifiedAt: string;
  /** ISO date ที่หมดอายุ */
  expiresAt: string;
  /** วันที่เหลือ — ติดลบคือหมดแล้ว */
  daysLeft: number;
  /** จำนวนครั้งที่ส่งการแจ้งเตือนไปแล้ว */
  remindersSent: number;
  lastReminderAt: string | null;
  /** ผู้ใช้เริ่มยืนยันรอบใหม่แล้วหรือยัง */
  renewalStarted: boolean;
  /** ยอดซื้อในรอบปีที่ใช้สิทธิ์นี้ (บาท) — ใช้ดูว่าเสียสิทธิ์แล้วกระทบเท่าไร */
  yearSpend: number;
}

function buildVerifications(): Verification[] {
  const out: Verification[] = [];
  const active = DOMAINS.filter((d) => d.enabled);
  for (let i = 0; i < 420; i += 1) {
    /* กระจายวันหมดอายุ: ส่วนใหญ่ยังไกล ที่เหลือใกล้หมดและหมดแล้ว */
    const bucket = i % 10;
    const daysLeft = bucket < 5 ? 90 + ((i * 7) % 240)
      : bucket < 8 ? 1 + ((i * 3) % 30)
        : -1 - ((i * 5) % 60);

    const d = active[i % active.length]!;
    const method: 'domain' | 'document' = i % 7 === 0 ? 'document' : 'domain';
    const expires = new Date(new Date('2026-08-21').getTime() + daysLeft * 86400000);
    const verified = new Date(expires.getTime() - VERIFICATION_DAYS * 86400000);
    const name = REQ_NAMES[i % REQ_NAMES.length]!;

    out.push({
      id: 'v' + i,
      member: {
        id: 'vm' + i, name, memberCode: 'MB-' + (9000 + i * 29),
        email: name.split(' ')[0]!.toLowerCase() + i + '@' + (method === 'domain' ? d.domain : 'gmail.com'),
        phone: '08' + String(20000000 + i * 41).slice(0, 8),
      },
      memberType: method === 'domain' ? d.memberType : 'ptt-retired',
      domainId: method === 'domain' ? d.id : null,
      method,
      verifiedAt: verified.toISOString().slice(0, 10),
      expiresAt: expires.toISOString().slice(0, 10),
      daysLeft,
      remindersSent: daysLeft < 0 ? 3 : daysLeft <= 7 ? 2 : daysLeft <= VERIFICATION_WARN_DAYS ? 1 : 0,
      lastReminderAt: daysLeft <= VERIFICATION_WARN_DAYS ? '2569-08-' + String(10 + (i % 10)).padStart(2, '0') : null,
      renewalStarted: daysLeft <= VERIFICATION_WARN_DAYS && i % 4 === 0,
      yearSpend: 2400 + ((i * 811) % 74000),
    });
  }
  return out;
}

export const VERIFICATIONS: Verification[] = buildVerifications();

/* ------------------------------------------------------------ โควตา */

/** หมวดสินค้าที่ถูกจำกัดปริมาณ — รายการจริงยังรอ (PROGRESS-REPORT 8.4) */
export type ProductCategory = 'vitamin' | 'supplement' | 'skincare' | 'medical-device' | 'other';

export const CATEGORY_LABEL: Record<ProductCategory, string> = {
  vitamin: 'วิตามินรวม',
  supplement: 'อาหารเสริมอื่น',
  skincare: 'ผลิตภัณฑ์ดูแลผิว',
  'medical-device': 'อุปกรณ์การแพทย์',
  other: 'สินค้าอื่น',
};

export interface QuotaRule {
  id: string;
  category: ProductCategory;
  memberType: MemberType;
  /** จำนวนชิ้นต่อปี — null = ไม่จำกัด */
  limitPerYear: number | null;
  /** เตือนผู้ใช้เมื่อใช้ถึงกี่เปอร์เซ็นต์ */
  warnAtPercent: number;
  enabled: boolean;
  /** ค่านี้เป็นค่าสมมติที่รอการยืนยันจากลูกค้าหรือไม่ */
  provisional: boolean;
  updatedAt: string;
  updatedBy: string;
}

export const QUOTA_RULES: QuotaRule[] = [
  { id: 'q1', category: 'vitamin', memberType: 'ptt-employee', limitPerYear: 500, warnAtPercent: 80, enabled: true, provisional: true, updatedAt: '2569-06-12', updatedBy: 'ธนกร แสงทวี' },
  { id: 'q2', category: 'vitamin', memberType: 'ptt-group', limitPerYear: 500, warnAtPercent: 80, enabled: true, provisional: true, updatedAt: '2569-06-12', updatedBy: 'ธนกร แสงทวี' },
  { id: 'q3', category: 'vitamin', memberType: 'ptt-retired', limitPerYear: null, warnAtPercent: 80, enabled: false, provisional: true, updatedAt: '2569-06-12', updatedBy: 'ธนกร แสงทวี' },
  { id: 'q4', category: 'supplement', memberType: 'ptt-employee', limitPerYear: 300, warnAtPercent: 80, enabled: true, provisional: true, updatedAt: '2569-06-12', updatedBy: 'ธนกร แสงทวี' },
  { id: 'q5', category: 'supplement', memberType: 'ptt-group', limitPerYear: 300, warnAtPercent: 80, enabled: true, provisional: true, updatedAt: '2569-06-12', updatedBy: 'ธนกร แสงทวี' },
  { id: 'q6', category: 'skincare', memberType: 'ptt-employee', limitPerYear: 200, warnAtPercent: 90, enabled: true, provisional: true, updatedAt: '2569-07-01', updatedBy: 'พิมพ์ชนก อารีย์' },
  { id: 'q7', category: 'skincare', memberType: 'ptt-group', limitPerYear: 200, warnAtPercent: 90, enabled: true, provisional: true, updatedAt: '2569-07-01', updatedBy: 'พิมพ์ชนก อารีย์' },
  { id: 'q8', category: 'medical-device', memberType: 'ptt-employee', limitPerYear: null, warnAtPercent: 80, enabled: false, provisional: true, updatedAt: '2569-07-01', updatedBy: 'พิมพ์ชนก อารีย์' },
  { id: 'q9', category: 'other', memberType: 'ptt-employee', limitPerYear: null, warnAtPercent: 80, enabled: false, provisional: true, updatedAt: '2569-06-12', updatedBy: 'ธนกร แสงทวี' },
];

export interface QuotaUsage {
  id: string;
  member: { id: string; name: string; memberCode: string };
  memberType: MemberType;
  category: ProductCategory;
  used: number;
  limit: number;
  /** ยอดเงินของส่วนที่ใช้ไป */
  amount: number;
  lastPurchaseAt: string;
  /** เคยถูกระบบบล็อกเพราะครบโควตากี่ครั้ง */
  blockedCount: number;
}

function buildUsage(): QuotaUsage[] {
  const out: QuotaUsage[] = [];
  const rules = QUOTA_RULES.filter((r) => r.enabled && r.limitPerYear !== null);
  for (let i = 0; i < 240; i += 1) {
    const rule = rules[i % rules.length]!;
    const limit = rule.limitPerYear as number;
    /* กระจายการใช้: ส่วนใหญ่ใช้น้อย บางส่วนใกล้เต็ม และมีที่เต็มแล้ว */
    const bucket = i % 12;
    const pct = bucket < 7 ? 5 + ((i * 3) % 45) : bucket < 10 ? 78 + ((i * 2) % 20) : 100;
    const used = Math.round(limit * pct / 100);
    const name = REQ_NAMES[i % REQ_NAMES.length]!;
    out.push({
      id: 'u' + i,
      member: { id: 'qm' + i, name, memberCode: 'MB-' + (5000 + i * 31) },
      memberType: rule.memberType,
      category: rule.category,
      used,
      limit,
      amount: used * (180 + (i % 7) * 40),
      lastPurchaseAt: '2569-08-' + String(1 + (i % 20)).padStart(2, '0'),
      blockedCount: pct >= 100 ? 1 + (i % 3) : 0,
    });
  }
  return out;
}

export const QUOTA_USAGE: QuotaUsage[] = buildUsage();

/* --------------------------------------------------------- audit log */

export function domainAudit(d: EmailDomain) {
  const entries: Array<Record<string, unknown>> = [
    {
      id: d.id + '-add', at: d.addedAt.replace('2569', '2026') + 'T09:12:00',
      actorName: d.addedBy, actorRole: 'ผู้ดูแลระบบ',
      action: 'เพิ่มโดเมน ' + d.domain, icon: 'add_circle',
      changes: [
        { field: 'ประเภทสมาชิกที่ผูก', before: null, after: MEMBER_TYPE_LABEL[d.memberType] },
        { field: 'นับโดเมนย่อย', before: null, after: d.includeSubdomains ? 'นับรวม' : 'ไม่นับ' },
      ],
    },
  ];
  if (!d.enabled && d.disabledAt) {
    entries.push({
      id: d.id + '-disable', at: d.disabledAt.replace('2569', '2026') + 'T14:38:00',
      actorName: 'ธนกร แสงทวี', actorRole: 'หัวหน้าทีมตรวจสอบ',
      action: 'ปิดใช้งานโดเมน ' + d.domain, icon: 'block',
      reason: d.disabledReason,
      changes: [
        { field: 'สถานะ', before: 'เปิดใช้งาน', after: 'ปิดใช้งาน' },
        { field: 'สมาชิกที่ยืนยันด้วยโดเมนนี้', before: null, after: d.disabledPolicy ? DISABLE_POLICY_LABEL[d.disabledPolicy] : '—' },
      ],
    });
  }
  return entries.reverse();
}

export function requestAudit(r: TypeRequest) {
  const entries: Array<Record<string, unknown>> = [
    {
      id: r.id + '-submit', at: r.submittedAt,
      actorName: r.member.name, actorRole: 'สมาชิก',
      action: 'ยื่นคำขอเปลี่ยนเป็น ' + MEMBER_TYPE_LABEL[r.requestedType], icon: 'upload',
      changes: [{ field: 'เอกสารแนบ', before: null, after: r.documents.length + ' ไฟล์' }],
    },
    {
      id: r.id + '-route', at: new Date(new Date(r.submittedAt).getTime() + 20000).toISOString(),
      actorName: 'ระบบ', actorRole: '—', automated: true,
      action: 'ส่งเข้าคิวคน — ' + MANUAL_REASON_LABEL[r.manualReason], icon: 'alt_route',
      changes: [],
    },
  ];
  if (r.decision) {
    entries.push({
      id: r.id + '-decide', at: r.decision.at,
      actorName: 'สุดารัตน์ ทองดี', actorRole: 'เจ้าหน้าที่ตรวจสอบ',
      action: r.status === 'approved' ? 'อนุมัติคำขอ' : 'ปฏิเสธคำขอ',
      icon: r.status === 'approved' ? 'check_circle' : 'cancel',
      reason: r.decision.reasonLabel,
      changes: [{ field: 'ประเภทสมาชิก', before: 'สมาชิกทั่วไป', after: r.status === 'approved' ? MEMBER_TYPE_LABEL[r.requestedType] : 'สมาชิกทั่วไป' }],
    });
  }
  return entries.reverse();
}

/* รองรับ: FR-ADM-08, FR-ADM-09, FR-ADM-17, BR-09, SRS 3.2, SRS 3.5, FR-MTYPE-01…06 */
